import { useSetAtom } from "jotai";
import { Icon } from "@common/index";
import { isShipToSwitcherOpenAtom } from "@/store/shipToAtoms";

interface RestrictedLinesNoticeProps {
    /** Number of cart lines the selected destination cannot receive. Renders nothing at 0. */
    count: number;
}

/**
 * #228 / E4-S12. Shown on the cart AND on checkout, from one source, because both pages now
 * render totals that leave these lines out and neither figure may appear without this.
 *
 * The wording is load-bearing. A restricted line is NOT dropped at checkout: CreateOrderCommand
 * answers 412 naming every restricted product, so the whole order is refused while any of them
 * is in the cart. So this must never read as "we'll skip those and put the rest through" — it
 * has to say the order cannot be placed at all, and that the totals are what the shopper would
 * pay once the lines are gone.
 */
export const RestrictedLinesNotice = ({ count }: RestrictedLinesNoticeProps) => {
    const setShipToSwitcherOpen = useSetAtom(isShipToSwitcherOpenAtom);

    if (count <= 0) return null;

    const isSingle = count === 1;

    return (
        // Explicit text-warning-content: the light theme sets --color-base-content to
        // white (see the CLAUDE.md theme quirk), so the alert must not inherit it.
        <div
            role="alert"
            data-testid="cart-restricted-notice"
            className="alert alert-warning text-warning-content"
        >
            <Icon name="block" filled />
            <div>
                <h2 className="font-bold">
                    {count} {isSingle ? "item can't" : "items can't"} ship to
                    your selected location
                </h2>
                <p>
                    This order can&apos;t be placed while{" "}
                    {isSingle ? "it's" : "they're"} in your cart — remove{" "}
                    {isSingle ? "it" : "them"} or ship to a different location.
                    The totals shown leave {isSingle ? "it" : "them"} out.
                </p>
            </div>
            {/* Explicit text-neutral for the same reason as the alert above: daisyUI
                takes the button foreground from --color-base-content, which the light
                theme sets to white (CLAUDE.md theme quirk). Without it the notice's
                only escape route renders white-on-white. Not btn-warning like
                ProductNotAvailable's equivalent control — that would disappear into
                this alert's own warning background. */}
            <button
                type="button"
                onClick={() => setShipToSwitcherOpen(true)}
                className="btn btn-sm text-neutral"
            >
                Change location
            </button>
        </div>
    );
};
